import { ReactNode, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import {
  Sparkles,
  Bot,
  Instagram,
  Send,
  MessageCircle,
  Globe,
  Zap,
  TrendingUp,
  Quote,
  Star,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import ThemeToggle from "@/components/ThemeToggle";
import AuthFormSkeleton from "./AuthSkeleton";
import { toPersianDigits } from "@/lib/auth/utils";

export interface AuthShellProps {
  children: ReactNode;
  /** Step number (1, 2, 3) shown as oversized indicator */
  step?: number;
  /** Total steps for stepper */
  totalSteps?: number;
  /** Eyebrow above the form heading */
  eyebrow?: string;
  /** Big heading shown in form panel */
  heading: ReactNode;
  /** Subheading description */
  subheading?: ReactNode;
  /** Optional footer slot below the card body */
  footer?: ReactNode;
}

/**
 * Bold split-screen auth shell.
 * - Right side (in RTL screen-right): immersive "showcase" with animated chat
 * - Left side: focused form panel with oversized step indicator
 * - On mobile: showcase collapses to a compact gradient header
 */
export default function AuthShell({
  children,
  step,
  totalSteps,
  eyebrow,
  heading,
  subheading,
  footer,
}: AuthShellProps) {
  const [autoReady, setAutoReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAutoReady(true), 700);
    return () => clearTimeout(t);
  }, []);
  const ready = autoReady;

  return (
    <div dir="rtl" className="relative min-h-screen overflow-hidden bg-background text-foreground">
      {/* Top bar */}
      <header className="absolute inset-x-0 top-0 z-30 flex items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <Link href="/" className="flex items-center gap-2">
          <BrandMark size={36} />
          <span className="hidden text-base font-extrabold tracking-tight text-foreground sm:inline">
            ترندی <span className="text-primary">اسمارت</span>
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <Link
            href="/"
            className="hidden rounded-full border border-border/70 bg-background/60 px-3 py-1.5 text-xs font-semibold text-muted-foreground backdrop-blur-md transition hover:border-primary/40 hover:text-primary sm:inline-flex"
          >
            ← بازگشت به سایت
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <div className="grid min-h-screen lg:grid-cols-[minmax(0,1fr)_minmax(0,560px)]">
        {/* SHOWCASE PANEL */}
        <ShowcasePanel ready={ready} />

        {/* FORM PANEL */}
        <main className="relative flex items-center justify-center px-4 pb-10 pt-24 sm:px-6 lg:px-10 lg:py-10">
          {/* Soft background glow on form side */}
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute right-0 top-1/2 h-[420px] w-[420px] -translate-y-1/2 translate-x-1/3 rounded-full bg-primary/10 blur-[120px]" />
            <div className="absolute bottom-0 left-1/4 h-[300px] w-[300px] rounded-full bg-secondary-main/10 blur-[120px]" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="relative w-full max-w-md"
          >
            {!ready ? (
              <AuthFormSkeleton />
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
              >
                {/* Step indicator */}
                {step && totalSteps && (
                  <div className="mb-6 flex items-end justify-between">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[64px] font-black leading-none text-foreground/90">
                        {toPersianDigits(step.toString().padStart(2, "0"))}
                      </span>
                      <span className="text-sm font-bold text-muted-foreground">
                        /{toPersianDigits(totalSteps.toString().padStart(2, "0"))}
                      </span>
                    </div>
                    <div className="flex flex-1 items-center justify-end gap-1.5 ps-4">
                      {Array.from({ length: totalSteps }).map((_, i) => (
                        <span
                          key={i}
                          className={`h-1.5 rounded-full transition-all duration-500 ${
                            i < step
                              ? "w-8 bg-gradient-to-l from-primary to-secondary-main"
                              : "w-4 bg-border"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Heading */}
                <header className="mb-7 space-y-2">
                  {eyebrow && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/8 px-2.5 py-1 text-[11px] font-bold tracking-wide text-primary">
                      <Sparkles className="h-3 w-3" /> {eyebrow}
                    </span>
                  )}
                  <h1 className="text-3xl font-black leading-tight text-foreground sm:text-[34px]">
                    {heading}
                  </h1>
                  {subheading && (
                    <p className="text-sm leading-relaxed text-muted-foreground">{subheading}</p>
                  )}
                </header>

                {/* Form body */}
                <div>{children}</div>

                {footer && <div className="mt-6">{footer}</div>}

                {/* Footer line */}
                <div className="mt-10 flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-4 text-[11px] text-muted-foreground">
                  <span>©{toPersianDigits(new Date().getFullYear())} ترندی اسمارت</span>
                  <div className="flex items-center gap-3">
                    <Link href="/privacy" className="hover:text-primary">
                      حریم خصوصی
                    </Link>
                    <Link href="/terms" className="hover:text-primary">
                      شرایط استفاده
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </main>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Showcase panel — bold gradient with animated chat & stats
// ──────────────────────────────────────────────────────────
function ShowcasePanel({ ready }: { ready: boolean }) {
  return (
    <aside className="relative hidden overflow-hidden lg:block">
      {/* Gradient backdrop */}
      <div className="absolute inset-0">
        {/* Always-dark brand gradient (panel is intentionally immersive) — but uses brand tones that match both themes */}
        <div className="absolute inset-0 bg-gradient-to-br from-[oklch(0.42_0.12_180)] via-[oklch(0.28_0.09_220)] to-[oklch(0.20_0.05_260)] dark:from-[oklch(0.32_0.10_180)] dark:via-[oklch(0.22_0.08_220)] dark:to-[oklch(0.18_0.04_260)]" />
        <div className="absolute -left-32 -top-32 h-[520px] w-[520px] rounded-full bg-primary/40 blur-[120px]" />
        <div className="absolute -bottom-32 -right-20 h-[520px] w-[520px] rounded-full bg-fuchsia-500/30 blur-[140px]" />
        <div className="absolute left-1/3 top-1/2 h-[300px] w-[300px] rounded-full bg-amber-400/20 blur-[120px]" />
        {/* Mesh grid */}
        <div className="absolute inset-0 opacity-[0.07] [background-image:linear-gradient(rgba(255,255,255,0.4)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.4)_1px,transparent_1px)] [background-size:48px_48px]" />
        {/* Noise */}
        <div className="absolute inset-0 opacity-[0.04] [background-image:url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><filter id=%22n%22><feTurbulence baseFrequency=%220.85%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22 opacity=%220.7%22/></svg>')]" />
      </div>

      <div className="relative flex h-full flex-col px-10 pb-10 pt-24 xl:px-14">
        {/* Top: tagline */}
        <div className="max-w-md">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur-md">
            <Sparkles className="h-3 w-3 text-amber-300" /> هوش مصنوعی فارسی‌زبان · نسخه{" "}
            {toPersianDigits(new Date().getFullYear())}
          </span>
          <h2 className="mt-5 text-[44px] font-black leading-[1.05] tracking-tight text-white xl:text-[52px]">
            مکالمه با مشتری‌ها،
            <br />
            <span className="bg-gradient-to-l from-amber-300 via-rose-300 to-fuchsia-300 bg-clip-text text-transparent">
              هوشمندتر از همیشه.
            </span>
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-white/70">
            دایرکت، کامنت و چت چندکانال — همه در یک داشبورد. هوش مصنوعی ترندی فارسی‌زبان جواب
            می‌دهد، شما فروش را می‌بندید.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {[
              { Icon: Instagram, label: "اینستاگرام" },
              { Icon: Send, label: "تلگرام" },
              { Icon: MessageCircle, label: "واتساپ" },
              { Icon: Globe, label: "وب‌سایت" },
            ].map(({ Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/8 px-3 py-1 text-xs font-semibold text-white/90 backdrop-blur-md"
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </span>
            ))}
          </div>
        </div>

        {/* Center: animated chat mock */}
        <div className="mt-8 flex-1">
          <ChatMock ready={ready} />
        </div>

        {/* Bottom: stats + testimonial */}
        <div className="mt-6 grid gap-3 xl:grid-cols-[1fr_auto]">
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: "۹۸٪", label: "نرخ پاسخ", Icon: TrendingUp },
              { value: "۲.۳x", label: "افزایش فروش", Icon: Sparkles },
              { value: "۲۴/۷", label: "آنلاین", Icon: Zap },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-2xl border border-white/15 bg-white/5 px-3 py-2.5 text-center backdrop-blur-md"
              >
                <s.Icon className="mx-auto mb-1 h-4 w-4 text-amber-300" />
                <div className="text-lg font-black text-white">{s.value}</div>
                <div className="text-[10px] text-white/60">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="hidden items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-3 backdrop-blur-md xl:flex">
            <Quote className="h-5 w-5 text-amber-300" />
            <div className="text-[11px] leading-snug text-white/85">
              «بعد از ترندی، نرخ بستن فروش دایرکت‌هام دو برابر شده.»
              <div className="mt-1 flex items-center gap-1 text-[10px] text-white/60">
                <Star className="h-3 w-3 fill-amber-300 text-amber-300" />
                <Star className="h-3 w-3 fill-amber-300 text-amber-300" />
                <Star className="h-3 w-3 fill-amber-300 text-amber-300" />
                <Star className="h-3 w-3 fill-amber-300 text-amber-300" />
                <Star className="h-3 w-3 fill-amber-300 text-amber-300" />
                <span className="ms-1">— نگار، فروشگاه نگار</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

// ─── Animated chat mock ───
const SCRIPT = [
  { who: "user", text: "سلام، شال طوسی موجوده؟ 😊" },
  { who: "ai", text: "سلام نگار جان! بله موجوده ✨ سایز رو بفرستید همین امروز ارسال میشه." },
  { who: "user", text: "عالی! یه عدد سایز ال، آدرس تهران." },
  { who: "ai", text: "ثبت شد ✅ کد پیگیری: TS-۸۲۹۱۵ — تا ۲۴ ساعت دستتونه." },
];

function ChatMock({ ready }: { ready: boolean }) {
  const [shown, setShown] = useState(1);

  useEffect(() => {
    if (!ready) return;
    const t = setInterval(() => {
      setShown((n) => (n >= SCRIPT.length ? 1 : n + 1));
    }, 2400);
    return () => clearInterval(t);
  }, [ready]);

  return (
    <div className="relative mx-auto max-w-md">
      {/* Floating phone frame */}
      <div className="absolute -inset-4 rounded-[36px] bg-gradient-to-br from-white/15 to-transparent blur-xl" />
      <div className="relative rounded-[28px] border border-white/15 bg-white/[0.06] p-4 shadow-2xl backdrop-blur-xl">
        {/* Header */}
        <div className="mb-3 flex items-center gap-3 border-b border-white/10 pb-3">
          <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-fuchsia-500 to-rose-500 text-white shadow-lg">
            <Instagram className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-white">@negar_shop</div>
            <div className="flex items-center gap-1 text-[10px] text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> آنلاین · پاسخ خودکار فعال
            </div>
          </div>
          <span className="rounded-full bg-amber-400/20 px-2 py-0.5 text-[10px] font-bold text-amber-300">
            DM
          </span>
        </div>

        {/* Messages — fixed height prevents layout shift as items append */}
        <div className="min-h-[260px] space-y-2.5 [direction:rtl]">
          {!ready ? (
            <ChatSkeletonMessages />
          ) : (
            <>
              <AnimatePresence initial={false}>
                {SCRIPT.slice(0, shown).map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex items-end gap-2 ${m.who === "ai" ? "flex-row-reverse" : ""}`}
                  >
                    <div
                      className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-white ${
                        m.who === "ai"
                          ? "bg-gradient-to-br from-primary to-emerald-500"
                          : "bg-gradient-to-br from-fuchsia-500 to-rose-500"
                      }`}
                    >
                      {m.who === "ai" ? (
                        <Bot className="h-3.5 w-3.5" />
                      ) : (
                        <span className="text-[10px] font-bold">ن</span>
                      )}
                    </div>
                    <div
                      className={`max-w-[80%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${
                        m.who === "ai"
                          ? "rounded-tr-sm bg-gradient-to-l from-primary/30 to-primary/10 text-white"
                          : "rounded-tl-sm bg-white/10 text-white"
                      }`}
                    >
                      {m.text}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {shown < SCRIPT.length && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2"
                >
                  <div className="grid h-7 w-7 place-items-center rounded-full bg-gradient-to-br from-primary to-emerald-500 text-white">
                    <Bot className="h-3.5 w-3.5" />
                  </div>
                  <div className="rounded-2xl rounded-tl-sm bg-white/10 px-3 py-2">
                    <div className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <motion.span
                          key={i}
                          animate={{ y: [0, -3, 0] }}
                          transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
                          className="h-1.5 w-1.5 rounded-full bg-white/70"
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3 text-[10px] text-white/60">
          <span className="inline-flex items-center gap-1">
            <Zap className="h-3 w-3 text-amber-300" /> پاسخ خودکار · ۱.۲ ثانیه
          </span>
          <span>قدرت‌گرفته از ترندی AI</span>
        </div>
      </div>
    </div>
  );
}

// Animated skeleton messages — shown in the chat mock during initial load
function ChatSkeletonMessages() {
  const rows: { who: "ai" | "user"; w: string }[] = [
    { who: "user", w: "w-[55%]" },
    { who: "ai", w: "w-[72%]" },
    { who: "user", w: "w-[45%]" },
    { who: "ai", w: "w-[68%]" },
  ];
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.12, duration: 0.3 }}
          className={`flex items-end gap-2 ${r.who === "ai" ? "flex-row-reverse" : ""}`}
        >
          <div className="h-7 w-7 shrink-0 rounded-full bg-white/10" />
          <div
            className={`relative ${r.w} max-w-[80%] overflow-hidden rounded-2xl bg-white/10 px-3 py-3 ${
              r.who === "ai" ? "rounded-tr-sm" : "rounded-tl-sm"
            }`}
          >
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: "100%" }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "linear", delay: i * 0.2 }}
              className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-white/15 to-transparent"
            />
          </div>
        </motion.div>
      ))}
    </div>
  );
}
